import {
  Bell,
  Building2,
  ChevronDown,
  Headphones,
  LogOut,
  Menu,
  Plus,
  Store,
  User as UserIcon,
} from "lucide-react";
import type { JSX } from "react";
import { useNavigate } from "react-router";

import { useSwitchTenantMutation } from "../../lib/api/auth.js";
import { useMyCompaniesQuery, type CompanyMembership } from "../../lib/api/users.js";
import { cn } from "../../lib/cn.js";
import { enterApp } from "../../lib/tenant-url.js";
import { useAuth } from "../../providers/auth-provider.js";
import { useSelectedStore } from "../../providers/selected-store-provider.js";
import {
  Avatar,
  AvatarFallback,
} from "../ui/avatar.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.js";

import { BrandMark } from "./BrandMark.js";

function displayName(email?: string): string {
  if (!email) return "Signed in";
  const [local] = email.split("@");
  return (local ?? email)
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function initials(email: string): string {
  const [local] = email.split("@");
  return (local ?? "?").slice(0, 2).toUpperCase();
}

function IconButton({
  ariaLabel,
  children,
  className,
}: {
  ariaLabel: string;
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={cn(
        "relative inline-flex h-10 w-10 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/10 hover:text-white",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/35",
        className,
      )}
    >
      {children}
    </button>
  );
}

const COMPANY_CHOSEN_KEY = "onshelf.companyChosen";
const SELECTED_STORE_KEY = "onshelf.selectedStoreId";

function CompanySwitcher(): JSX.Element | null {
  const { user } = useAuth();
  const companiesQuery = useMyCompaniesQuery();
  const switchMut = useSwitchTenantMutation();

  const companies = companiesQuery.data?.items ?? [];

  // Single-company (or fewer) users do not need a switcher.
  if (companies.length <= 1) return null;

  const activeCompany =
    companies.find((c) => c.tenantId === user?.tenantId) ?? null;

  const handleSwitch = async (company: CompanyMembership): Promise<void> => {
    if (company.tenantId === user?.tenantId) return;
    // useSwitchTenantMutation's onSuccess already does both of the things
    // we need: it sets qk.auth.me to the new user (so useAuth reflects
    // the new tenantId immediately) AND it removeQueries for everything
    // non-auth (so tenant-scoped caches refetch on the new cookie). The
    // previous queryClient.clear() + refetchQueries(auth.me) here was
    // wiping the just-set me data and re-fetching async — during the
    // gap useAuth saw user=null and CompanyGate (running on the next
    // render) bounced to the wrong path. Dropped both.
    const res = await switchMut.mutateAsync({ tenantId: company.tenantId });
    try {
      localStorage.setItem(COMPANY_CHOSEN_KEY, "1");
    } catch {
      /* ignore disabled storage */
    }
    try {
      localStorage.removeItem(SELECTED_STORE_KEY);
    } catch {
      /* ignore disabled storage */
    }
    // The new tenant lives under a different basename (/t/<newCode>), so a
    // router navigate would stay on the old slug — full-navigate into the new
    // tenant's app instead.
    enterApp(res.user.tenantCode);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Switch company"
          disabled={switchMut.isPending}
          className={cn(
            "inline-flex h-10 min-w-0 max-w-[min(40vw,15rem)] items-center gap-2 rounded-lg border border-white/10 bg-white/[0.07] pl-1.5 pr-2.5 text-left text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition-colors",
            "hover:bg-white/[0.11] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/35",
            "disabled:opacity-60",
          )}
        >
          <Avatar className="h-8 w-8 rounded-md border border-white/10">
            <AvatarFallback className="rounded-md bg-white/15 text-white">
              <Building2 className="h-4 w-4" aria-hidden="true" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 leading-tight">
            <span className="block max-w-44 truncate text-sm font-semibold">
              {activeCompany?.tenantName ?? "Select company"}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 text-white/55" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Switch company</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {companies.map((c) => (
          <DropdownMenuItem
            key={c.tenantId}
            onSelect={() => void handleSwitch(c)}
            className={c.tenantId === user?.tenantId ? "bg-muted" : undefined}
          >
            <Building2 className="mr-2 h-4 w-4" aria-hidden="true" />
            <span className="truncate font-medium">{c.tenantName}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Store picker pill — extracted so it can render in two places:
 *   - left brand block (md+), next to "Onshelf"
 *   - right cluster (<md fallback), where it sat originally
 *
 * The two instances share state via the SelectedStoreProvider so opening one
 * dropdown reflects the same active store in both placements.
 */
function StoreSwitcher({ className }: { className?: string }): JSX.Element {
  const navigate = useNavigate();
  const { stores, selectedStore, setSelectedStoreId } = useSelectedStore();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Switch store"
          className={cn(
            "inline-flex h-10 min-w-0 max-w-[min(46vw,17rem)] items-center gap-2 rounded-lg border border-white/10 bg-white/[0.07] pl-1.5 pr-2.5 text-left text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition-colors",
            "hover:bg-white/[0.11] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/35",
            className,
          )}
        >
          <Avatar className="h-8 w-8 rounded-md border border-white/10">
            <AvatarFallback className="rounded-md bg-white/15 text-[11px] font-semibold text-white">
              {selectedStore?.code ? selectedStore.code.slice(0, 2).toUpperCase() : "OS"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 leading-tight">
            <span className="block max-w-52 truncate text-sm font-semibold">
              {selectedStore?.name ?? "No store selected"}
            </span>
            {selectedStore && (
              <span className="block truncate text-xs text-white/60">
                {selectedStore.code}
              </span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 text-white/55" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Switch store</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {stores.length === 0 ? (
          <DropdownMenuItem disabled className="text-muted-foreground">
            No stores yet
          </DropdownMenuItem>
        ) : (
          stores.map((s) => (
            <DropdownMenuItem
              key={s.id}
              onSelect={() => setSelectedStoreId(s.id)}
              className={selectedStore?.id === s.id ? "bg-muted" : undefined}
            >
              <div className="flex flex-col">
                <span className="font-medium">{s.name}</span>
                <span className="text-xs text-muted-foreground">{s.code}</span>
              </div>
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void navigate("/settings/stores/create")}>
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          Add store
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void navigate("/settings/stores")}>
          <Store className="mr-2 h-4 w-4" aria-hidden="true" />
          Manage stores
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface AppTopbarProps {
  /** Opens the mobile navigation drawer; only used below the `md` breakpoint. */
  onMenuClick?: () => void;
}

export function AppTopbar({ onMenuClick }: AppTopbarProps = {}): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const userName = displayName(user?.email);

  const handleLogout = async (): Promise<void> => {
    await logout();
    void navigate("/login");
  };

  return (
    <header className="sticky top-0 z-20 flex h-[56px] items-center gap-2 bg-[#141414] px-3 sm:gap-3 sm:px-5">
      <button
        type="button"
        aria-label="Open navigation"
        onClick={onMenuClick}
        className={cn(
          "inline-flex h-10 w-10 items-center justify-center rounded-md text-white/90 transition-colors hover:bg-white/10 hover:text-white md:hidden",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/35",
        )}
      >
        <Menu className="h-5 w-5" aria-hidden="true" />
      </button>
      {/* Brand lockup: [logo] | Onshelf | [store pill] — desktop only;
          mobile shows the hamburger instead and the store pill drops into
          the right cluster (see the md:hidden instance below) so it stays
          reachable on narrow viewports. */}
      <div className="hidden items-center gap-2.5 md:flex">
        <BrandMark className="h-8 w-8 text-white" />
        <span className="mx-1.5 h-6 w-px bg-white/25" aria-hidden="true" />
        <span className="font-brand text-[28px] font-normal leading-none tracking-tight text-white">
          Onshelf
        </span>
        <span className="mx-1.5 h-6 w-px bg-white/25" aria-hidden="true" />
        <StoreSwitcher />
      </div>
      <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-2">
        <IconButton ariaLabel="Support" className="hidden sm:inline-flex">
          <Headphones className="h-5 w-5" aria-hidden="true" />
        </IconButton>
        <IconButton ariaLabel="Notifications">
          <Bell className="h-5 w-5" aria-hidden="true" />
          {/* Static '4+' badge — placeholder until notifications API lands */}
          <span className="absolute right-0.5 top-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-[#141414]">
            4+
          </span>
        </IconButton>
        <CompanySwitcher />
        {/* Mobile fallback for the store pill — the desktop instance lives
            in the brand block next to "Onshelf". */}
        <StoreSwitcher className="md:hidden" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Account menu"
              className={cn(
                "inline-flex h-10 min-w-0 items-center gap-2 rounded-lg px-1.5 py-1 text-left text-white transition-colors hover:bg-white/10",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/35",
              )}
            >
              <Avatar className="h-8 w-8 rounded-md">
                <AvatarFallback className="rounded-md bg-white text-xs font-semibold text-[#141414]">
                  {user ? initials(user.email) : "?"}
                </AvatarFallback>
              </Avatar>
              <div className="hidden min-w-0 leading-tight md:block">
                <span className="block max-w-40 truncate text-xs font-semibold">
                  {user?.email ?? userName}
                </span>
              </div>
              <ChevronDown
                className="hidden h-3.5 w-3.5 text-white/55 md:block"
                aria-hidden="true"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{user?.email ?? "Signed in"}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void navigate("/settings/account")}>
              <UserIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              Account
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {/* Switch company: /select-company is a root page on the public
                router (outside the tenant /t/<code> basename), so full-navigate
                instead of router navigate. The ?switch=1 flag tells that page to
                always show the picker — even for single-company users, who it
                would otherwise auto-bounce straight back into the app. */}
            <DropdownMenuItem
              onSelect={() => {
                window.location.assign("/select-company?switch=1");
              }}
            >
              <Building2 className="mr-2 h-4 w-4" aria-hidden="true" />
              Switch company
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void handleLogout()}>
              <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
